import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import "../../../../assets/css/Calendar.css";
import { showToastNotification } from "../../../../components/common/ui/NotificationModal/ToastNotication";

// ---- small pure helpers (no side effects) ----
const getTimeParts = (date) => {
  if (!date) return { hour: null, minute: null, ampm: null };
  const h = date.getHours();
  const ampm = h >= 12 ? "PM" : "AM";
  let hour12 = h % 12;
  if (hour12 === 0) hour12 = 12;
  return { hour: hour12, minute: date.getMinutes(), ampm };
};

const buildDateWithTime = (baseDate, hour12, minute, ampm) => {
  const base = baseDate ? new Date(baseDate) : new Date();
  let hour24 = hour12 % 12;
  if (ampm === "PM") hour24 += 12;
  base.setHours(hour24, minute, 0, 0);
  return base;
};

const toMinutesOfDay = (date) => date.getHours() * 60 + date.getMinutes();

const timeToMinutes = (hour12, minute, ampm) => {
  let hour24 = hour12 % 12;
  if (ampm === "PM") hour24 += 12;
  return hour24 * 60 + minute;
};

const isWithinRange = (totalMinutes, minTime, maxTime) => {
  if (minTime && totalMinutes < toMinutesOfDay(minTime)) return false;
  if (maxTime && totalMinutes > toMinutesOfDay(maxTime)) return false;
  return true;
};

const getDateKey = (d) => (d ? format(d, "yyyy-MM-dd") : null);

const resolveValidTime = (
  hour,
  minute,
  ampm,
  minTime,
  maxTime,
  hourList,
  minuteList,
) => {
  const hours = hour !== null ? [hour] : hourList;
  const ampms = ampm !== null ? [ampm] : ["AM", "PM"];
  const minutes = minute !== null ? [minute] : minuteList;
  for (const h of hours) {
    for (const ap of ampms) {
      for (const m of minutes) {
        if (isWithinRange(timeToMinutes(h, m, ap), minTime, maxTime)) {
          return { hour: h, minute: m, ampm: ap };
        }
      }
    }
  }
  return {
    hour: hour ?? hours[0],
    minute: minute ?? minutes[0],
    ampm: ampm ?? ampms[0],
  };
};

// ---- the 3-column Hour / Minute / AM-PM picker ----
const TimeColumnsPicker = ({
  value,
  handleChange,
  minuteInterval,
  minTime,
  maxTime,
  manualHour,
  manualMinute,
  manualAmpm,
  onHourManualSelect,
  onMinuteManualSelect,
  onAmpmManualSelect,
}) => {
  const hasValue = Boolean(value);
  const {
    hour: rawHour,
    minute: rawMinute,
    ampm: rawAmpm,
  } = getTimeParts(value);

  const selHour = manualHour ? rawHour : null;
  const selMinute = manualMinute ? rawMinute : null;
  const selAmpm = manualAmpm ? rawAmpm : null;

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from(
    { length: Math.ceil(60 / minuteInterval) },
    (_, i) => i * minuteInterval,
  );
  const ampmOptions = ["AM", "PM"];

  const isHourDisabled = (h) => {
    if (!hasValue) return true;
    const ampmCandidates = selAmpm ? [selAmpm] : ampmOptions;
    const minuteCandidates = selMinute !== null ? [selMinute] : minutes;
    return !ampmCandidates.some((ap) =>
      minuteCandidates.some((m) =>
        isWithinRange(timeToMinutes(h, m, ap), minTime, maxTime),
      ),
    );
  };

  const isMinuteDisabled = (m) => {
    if (!hasValue) return true;
    const ampmCandidates = selAmpm ? [selAmpm] : ampmOptions;
    const hourCandidates = selHour !== null ? [selHour] : hours;
    return !ampmCandidates.some((ap) =>
      hourCandidates.some((h) =>
        isWithinRange(timeToMinutes(h, m, ap), minTime, maxTime),
      ),
    );
  };

  const isAmPmDisabled = (ap) => {
    if (!hasValue) return true;
    const hourCandidates = selHour !== null ? [selHour] : hours;
    const minuteCandidates = selMinute !== null ? [selMinute] : minutes;
    return !hourCandidates.some((h) =>
      minuteCandidates.some((m) =>
        isWithinRange(timeToMinutes(h, m, ap), minTime, maxTime),
      ),
    );
  };

  const onHourClick = (h) => {
    if (isHourDisabled(h)) return;
    const resolved = resolveValidTime(
      h,
      selMinute,
      selAmpm,
      minTime,
      maxTime,
      hours,
      minutes,
    );
    onHourManualSelect?.();
    handleChange(
      buildDateWithTime(value, resolved.hour, resolved.minute, resolved.ampm),
    );
  };
  const onMinuteClick = (m) => {
    if (isMinuteDisabled(m)) return;
    const resolved = resolveValidTime(
      selHour,
      m,
      selAmpm,
      minTime,
      maxTime,
      hours,
      minutes,
    );
    onMinuteManualSelect?.();
    handleChange(
      buildDateWithTime(value, resolved.hour, resolved.minute, resolved.ampm),
    );
  };
  const onAmPmClick = (ap) => {
    if (isAmPmDisabled(ap)) return;
    const resolved = resolveValidTime(
      selHour,
      selMinute,
      ap,
      minTime,
      maxTime,
      hours,
      minutes,
    );
    onAmpmManualSelect?.();
    handleChange(
      buildDateWithTime(value, resolved.hour, resolved.minute, resolved.ampm),
    );
  };

  const scrollIntoViewIfSelected = (el, isSelected) => {
    if (isSelected && el) {
      setTimeout(() => el.scrollIntoView({ block: "nearest" }), 0);
    }
  };

  return (
    <div className="cpo-time-columns">
      <div className="cpo-time-col">
        {hours.map((h) => {
          const disabled = isHourDisabled(h);
          const isSelected = hasValue && manualHour && h === selHour;
          return (
            <div
              key={`hr-${h}`}
              ref={(el) => scrollIntoViewIfSelected(el, isSelected)}
              className={`cpo-time-item ${isSelected ? "selected" : ""} ${disabled ? "disabled" : ""
                }`}
              onClick={() => onHourClick(h)}
            >
              {String(h).padStart(2, "0")}
            </div>
          );
        })}
      </div>
      <div className="cpo-time-col">
        {minutes.map((m) => {
          const disabled = isMinuteDisabled(m);
          const isSelected = hasValue && manualMinute && m === selMinute;
          return (
            <div
              key={`min-${m}`}
              ref={(el) => scrollIntoViewIfSelected(el, isSelected)}
              className={`cpo-time-item ${isSelected ? "selected" : ""} ${disabled ? "disabled" : ""
                }`}
              onClick={() => onMinuteClick(m)}
            >
              {String(m).padStart(2, "0")}
            </div>
          );
        })}
      </div>
      <div className="cpo-time-col cpo-ampm-col">
        {ampmOptions.map((ap) => {
          const disabled = isAmPmDisabled(ap);
          const isSelected = hasValue && manualAmpm && ap === selAmpm;
          return (
            <div
              key={ap}
              ref={(el) => scrollIntoViewIfSelected(el, isSelected)}
              className={`cpo-time-item ${isSelected ? "selected" : ""} ${disabled ? "disabled" : ""
                }`}
              onClick={() => onAmPmClick(ap)}
            >
              {ap}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DatePickerComponent = ({
  name,
  label,
  ariaLabel = "",
  value,
  handleChange,
  maxDate,
  minDate,
  minTime,
  maxTime,
  errorMessage = {},
  required = false,
  disabled = false,
  showYearDropdown = true,
  showMonthDropdown = true,
  isClearable = true,
  preventClear = false,
  handleBlur,
  handleKeyDown,
  showTimeSelect = false,
  minuteInterval = 5,
  "aria-label": ariaLabelProp,
  "aria-required": ariaRequiredProp,
}) => {
  const isRequired = ariaRequiredProp !== undefined ? ariaRequiredProp : required;
  const ariaLabelText =
    ariaLabelProp ||
    ariaLabel ||
    (typeof label === "string" && label.trim() ? label.trim() : "") ||
    (typeof name === "string" && name.trim() ? name.trim() : "") ||
    "Select a date";

  const [inputValue, setInputValue] = useState("");
  const [isMonthOpen, setIsMonthOpen] = useState(false);
  const [isYearOpen, setIsYearOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  // Fully controlled open state.
  const [isOpen, setIsOpen] = useState(false);

  const [manualHour, setManualHour] = useState(Boolean(value));
  const [manualMinute, setManualMinute] = useState(Boolean(value));
  const [manualAmpm, setManualAmpm] = useState(Boolean(value));

  const prevDateKeyRef = useRef(getDateKey(value));
  const didInternalDateChangeRef = useRef(false);
  const isClosingRef = useRef(false);
  const justCompletedViaClickRef = useRef(false);

  const instanceIdRef = useRef(
    `dp-popper-${Math.random().toString(36).slice(2, 9)}`,
  );

  const containerRef = useRef(null);

  useEffect(() => {
    setInputValue(
      value
        ? format(value, showTimeSelect ? "MM/dd/yyyy hh:mm aa" : "MM/dd/yyyy")
        : "",
    );
    if (!value) {
      setResetKey((prev) => prev + 1);
    }
  }, [value, showTimeSelect]);

  useEffect(() => {
    const newDateKey = getDateKey(value);
    if (newDateKey !== prevDateKeyRef.current) {
      if (didInternalDateChangeRef.current) {
        didInternalDateChangeRef.current = false;
      } else {
        const hasVal = Boolean(value);
        setManualHour(hasVal);
        setManualMinute(hasVal);
        setManualAmpm(hasVal);
      }
      prevDateKeyRef.current = newDateKey;
    }
  }, [value]);

  const handleDateChange = (selectedDate) => {
    if (selectedDate) {
      const prevDateKey = getDateKey(value);
      const newDateKey = getDateKey(selectedDate);
      if (showTimeSelect && newDateKey !== prevDateKey) {
        const resetDate = new Date(selectedDate);
        resetDate.setHours(0, 0, 0, 0);
        didInternalDateChangeRef.current = true;
        setManualHour(false);
        setManualMinute(false);
        setManualAmpm(false);
        handleChange(resetDate);
        return;
      }
      handleChange(selectedDate);
    } else if (!preventClear) {
      didInternalDateChangeRef.current = true;
      setManualHour(false);
      setManualMinute(false);
      setManualAmpm(false);
      handleChange(null);
      setIsOpen(true);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData("text").trim();
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;

    if (dateRegex.test(pasteData)) {
      event.target.value = pasteData;
      handleChange(new Date(pasteData));
    }
  };

  const validateTimeCompletion = () => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    setTimeout(() => {
      isClosingRef.current = false;
    }, 0);

    if (!showTimeSelect) return;
    const isComplete =
      Boolean(value) && manualHour && manualMinute && manualAmpm;
    if (!isComplete) {
      didInternalDateChangeRef.current = true;
      setManualHour(false);
      setManualMinute(false);
      setManualAmpm(false);
      handleChange(null);
      showToastNotification(
        "Please select Date, Hour, Minute and AM/PM completely",
        "warning",
      );
    }
  };

  const validateTimeCompletionRef = useRef(validateTimeCompletion);
  validateTimeCompletionRef.current = validateTimeCompletion;

  // Disabled auto-close on selection completion so datepicker remains open until user clicks Close button
  // useEffect(() => {
  //   if (
  //     isOpen &&
  //     showTimeSelect &&
  //     manualHour &&
  //     manualMinute &&
  //     manualAmpm &&
  //     value &&
  //     justCompletedViaClickRef.current
  //   ) {
  //     justCompletedViaClickRef.current = false;
  //     setIsOpen(false);
  //   }
  // }, [isOpen, showTimeSelect, manualHour, manualMinute, manualAmpm, value]);

  useEffect(() => {
    if (!isOpen) return;

    const handleDocMouseDown = (e) => {
      const target = e.target;
      const insideOwnContainer = containerRef.current?.contains(target);
      const insideOwnPopper = target.closest?.(`.${instanceIdRef.current}`);
      if (!insideOwnContainer && !insideOwnPopper) {
        validateTimeCompletionRef.current();
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocMouseDown, true);
    return () => {
      document.removeEventListener("mousedown", handleDocMouseDown, true);
    };
  }, [isOpen]);

  return (
    <div className="datepicker-container" ref={containerRef}>
      {label && (
        <label className="col-md-3 w-100 form-label inmate-label">
          {label}
          {required && <span className="text-danger">*</span>}
        </label>
      )}
      <div>
        <DatePicker
          key={resetKey}
          name={name}
          readOnly
          disabled={disabled}
          selected={value}
          openToDate={value && !isNaN(new Date(value).getTime()) ? new Date(value) : new Date()}
          onChange={handleDateChange}
          onBlur={handleBlur}
          dateFormat={showTimeSelect ? "MM/dd/yyyy hh:mm aa" : "MM/dd/yyyy"}
          placeholderText="MM/DD/YYYY"
          showTimeSelect={false}
          isClearable={isClearable}
          showYearDropdown={showYearDropdown}
          scrollableYearDropdown
          showMonthDropdown={showMonthDropdown}
          scrollableMonthYearDropdown
          maxDate={maxDate}
          minDate={minDate}
          minTime={minTime}
          maxTime={maxTime}
          autoComplete="off"
          disabledKeyboardNavigation
          timeIntervals={minuteInterval}
          onKeyDown={handleKeyDown}
          open={isOpen}
          popperClassName={instanceIdRef.current}
          onInputClick={() => {
            justCompletedViaClickRef.current = false;
            if (!value) {
              setResetKey((prev) => prev + 1);
            }
            setIsOpen(true);
          }}
          onCalendarOpen={() => {
            setIsYearOpen(false);
            setIsMonthOpen(false);
          }}
          onCalendarClose={() => setIsOpen(false)}
          shouldCloseOnSelect={false}
          customInput={
            <input
              className={`form-control fs-5 fw-normal ${errorMessage[name] ? "is-invalid" : ""
                }`}
              value={inputValue}
              readOnly
              onKeyDown={handleKeyDown}
              maxLength={10}
              placeholder="Select a date"
              onPaste={handlePaste}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              autoComplete="off"
              onContextMenu={(e) => e.preventDefault()}
              aria-label={ariaLabelText}
              aria-required={isRequired}
            />
          }
          renderCustomHeader={({
            date,
            changeYear,
            changeMonth,
            decreaseMonth,
            increaseMonth,
          }) => {
            const currentYear = new Date(date).getFullYear();
            const currentMonth = new Date(date).getMonth();

            const months = [
              "Jan",
              "Feb",
              "Mar",
              "Apr",
              "May",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Oct",
              "Nov",
              "Dec",
            ];

            const minMonth = minDate ? new Date(minDate).getMonth() : 0;
            const maxMonth = maxDate ? new Date(maxDate).getMonth() : 11;
            const minYear = minDate
              ? new Date(minDate).getFullYear()
              : currentYear - 125;
            const maxYear = maxDate
              ? new Date(maxDate).getFullYear()
              : currentYear + 125;

            const allowedMonths = months.filter((_, index) => {
              if (currentYear === minYear && currentYear === maxYear) {
                return index >= minMonth && index <= maxMonth;
              }
              if (currentYear === minYear) {
                return index >= minMonth;
              }
              if (currentYear === maxYear) {
                return index <= maxMonth;
              }
              return true;
            });

            const selectedMonthName = months[currentMonth];
            if (!allowedMonths.includes(selectedMonthName)) {
              const firstAllowedMonthName = allowedMonths[0];
              const newMonthIndex = months.indexOf(firstAllowedMonthName);
              changeMonth(newMonthIndex);
            }
            const years = Array.from(
              { length: maxYear - minYear + 1 },
              (_, i) => minYear + i,
            );

            return (
              <div className="react-datepicker__header__dropdown react-datepicker__header__dropdown--scroll">
                <div className="custom-header-wrapper">
                  <button
                    className="arrow-icon"
                    onClick={decreaseMonth}
                    disabled={
                      currentMonth === minMonth && currentYear === minYear
                    }
                  >
                    <span style={{ fontSize: "20px", fontWeight: 800 }}>
                      {currentMonth === minMonth && currentYear === minYear
                        ? ""
                        : "<"}
                    </span>
                  </button>

                  <div className="custom-dropdown month-dropdown">
                    <div
                      className="dropdown-toggle"
                      onClick={() => {
                        setIsMonthOpen(!isMonthOpen);
                        setIsYearOpen(false);
                      }}
                    >
                      {months[currentMonth]}
                      <span className="arrow-icon-dropdown">
                        {isMonthOpen ? "▲" : "▼"}
                      </span>
                    </div>
                    {isMonthOpen && (
                      <div className="dropdown-menu scrollable">
                        {allowedMonths.map((month, index) => {
                          const isSelected =
                            months.indexOf(month) === currentMonth;
                          return (
                            <div
                              key={index}
                              ref={(el) => {
                                if (isSelected && el) {
                                  setTimeout(
                                    () =>
                                      el.scrollIntoView({ block: "nearest" }),
                                    0,
                                  );
                                }
                              }}
                              className={`dropdown-item ${isSelected ? "selected" : ""
                                }`}
                              onClick={() => {
                                changeMonth(months.indexOf(month));
                                setIsMonthOpen(false);
                              }}
                            >
                              {month}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="custom-dropdown year-dropdown">
                    <div
                      className="dropdown-toggle"
                      onClick={() => {
                        setIsYearOpen(!isYearOpen);
                        setIsMonthOpen(false);
                      }}
                    >
                      {currentYear}
                      <span className="arrow-icon-dropdown">
                        {isYearOpen ? "▲" : "▼"}
                      </span>
                    </div>
                    {isYearOpen && (
                      <div className="dropdown-menu scrollable">
                        {years.map((year) => {
                          const isSelected = year === currentYear;
                          return (
                            <div
                              key={year}
                              ref={(el) => {
                                if (isSelected && el) {
                                  setTimeout(
                                    () =>
                                      el.scrollIntoView({ block: "nearest" }),
                                    0,
                                  );
                                }
                              }}
                              className={`dropdown-item ${isSelected ? "selected" : ""
                                }`}
                              onClick={() => {
                                changeYear(year);
                                setIsYearOpen(false);
                              }}
                            >
                              {year}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <button
                    className="arrow-icon"
                    onClick={increaseMonth}
                    disabled={
                      currentMonth === maxMonth && currentYear === maxYear
                    }
                  >
                    <span style={{ fontSize: "20px", fontWeight: 800 }}>
                      {currentMonth === maxMonth && currentYear === maxYear
                        ? ""
                        : ">"}
                    </span>
                  </button>
                </div>
              </div>
            );
          }}
          portalId="root"
          popperPlacement="top-start"
        >
          {showTimeSelect && (
            <TimeColumnsPicker
              value={value}
              handleChange={handleChange}
              minuteInterval={minuteInterval}
              minTime={minTime}
              maxTime={maxTime}
              manualHour={manualHour}
              manualMinute={manualMinute}
              manualAmpm={manualAmpm}
              onHourManualSelect={() => {
                justCompletedViaClickRef.current = true;
                setManualHour(true);
              }}
              onMinuteManualSelect={() => {
                justCompletedViaClickRef.current = true;
                setManualMinute(true);
              }}
              onAmpmManualSelect={() => {
                justCompletedViaClickRef.current = true;
                setManualAmpm(true);
              }}
            />
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "8px 12px",
              borderTop: "1px solid #e9ecef",
              backgroundColor: "#f8f9fa",
            }}
          >
            <button
              type="button"
              className="btn button-primary btn-sm"
              style={{
                fontSize: "13px",
                padding: "4px 16px",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              onClick={() => {
                validateTimeCompletion();
                setIsOpen(false);
              }}
            >
              Okay
            </button>
          </div>
        </DatePicker>
        {errorMessage && Object.keys(errorMessage).length > 0 && (
          <div className="text-danger fw-700 fs-6">{errorMessage[name]}</div>
        )}
      </div>
    </div>
  );
};

export default DatePickerComponent;
